import React, { useEffect, useState, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import api from "../utils/axios";
import { useNavigate } from "react-router-dom";
import UserRowSkeleton from "../components/Skeleton/UserRowSkeleton";
import Swal from "sweetalert2";
import {
  Users,
  QrCode,
  Download,
  Copy,
  Check,
  RefreshCw,
  ExternalLink,
  Plus,
  Search,
  X,
  Store,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  TrendingUp,
  Wallet
} from "lucide-react";
import { formatCurrency } from "../helpers";

interface Agent {
  id: number | string;
  name: string;
  email: string;
  password2?: string;
  referalcode?: string;
  referral_link?: string;
  qr_code?: string;
  qr_link?: string;
  qr_image_url?: string;
  is_collaborator?: boolean;
  is_blocked?: boolean;
  created?: string;
  actual_total_deposit_amount?: number;
  actual_total_withdrawal_amount?: number;
  available_inr?: number;
  total_players_count?: number;
  qr_players_count?: number;
}

const Agent: React.FC = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState<Agent[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Add Agent Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newCollaborator, setNewCollaborator] = useState({
    username: "",
    email: "",
    password: "",
  });

  // QR Modal State
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    fetchCollaborators();
  }, [page]);

  const fetchCollaborators = async () => {
    setLoading(true);
    try {
      const res = await api.get("/collaborators/list", {
        params: { page, limit, search: searchTerm },
      });

      setUsers(res.data.users || []);
      setTotalUsers(res.data.totalUsers || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error("Failed to fetch collaborators:", err);
      toast.error("Failed to fetch agents list");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchCollaborators();
  };

  const handleAddCollaborator = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCollaborator.username || !newCollaborator.email || !newCollaborator.password) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsAdding(true);
    try {
      const res = await api.post("/create/collaborators", newCollaborator);
      toast.success("Agent/Store created successfully with unique QR code!");
      setNewCollaborator({ username: "", email: "", password: "" });
      setIsAddModalOpen(false);
      fetchCollaborators();

      // Automatically open the QR modal for the newly created agent
      if (res.data?.data) {
        setSelectedAgent(res.data.data);
        setIsQRModalOpen(true);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to add agent");
    } finally {
      setIsAdding(false);
    }
  };

  const handleOpenQRModal = (agent: Agent) => {
    const qrCode = agent.qr_code || `AG_${agent.id}`;
    const qrLink = agent.qr_link || `https://plyzoo.com/referal/${agent.referalcode}?agent_code=${qrCode}&source=qr`;
    const qrImageUrl = agent.qr_image_url || `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrLink)}`;

    setSelectedAgent({
      ...agent,
      qr_code: qrCode,
      qr_link: qrLink,
      qr_image_url: qrImageUrl,
    });
    setCopiedLink(false);
    setIsQRModalOpen(true);
  };

  const handleCopyLink = () => {
    if (!selectedAgent?.qr_link) return;
    navigator.clipboard.writeText(selectedAgent.qr_link);
    setCopiedLink(true);
    toast.success("Referral QR link copied to clipboard!");
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const handleDownloadQR = async () => {
    if (!selectedAgent) return;

    try {
      const qrImageUrl = selectedAgent.qr_image_url ||
        `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(selectedAgent.qr_link || '')}`;

      const response = await fetch(qrImageUrl);
      const blob = await response.blob();

      // Create a canvas with high quality branding banner
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = URL.createObjectURL(blob);

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const width = 400;
        const height = 480;

        canvas.width = width;
        canvas.height = height;

        if (ctx) {
          // White background
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);

          // Top Header banner
          ctx.fillStyle = "#059669"; // Emerald 600
          ctx.fillRect(0, 0, width, 60);

          // Header Text
          ctx.fillStyle = "#ffffff";
          ctx.font = "bold 20px Inter, sans-serif";
          ctx.textAlign = "center";
          ctx.fillText("PlyZoo Store / Agent QR", width / 2, 38);

          // Draw QR Code in center
          const qrSize = 280;
          const qrX = (width - qrSize) / 2;
          const qrY = 80;
          ctx.drawImage(img, qrX, qrY, qrSize, qrSize);

          // Agent Name & Store ID footer
          ctx.fillStyle = "#111827";
          ctx.font = "bold 16px Inter, sans-serif";
          ctx.fillText(selectedAgent.name, width / 2, 390);

          ctx.fillStyle = "#6B7280";
          ctx.font = "12px monospace";
          ctx.fillText(`Code: ${selectedAgent.qr_code || selectedAgent.referalcode} • UID: #${selectedAgent.id}`, width / 2, 415);

          ctx.fillStyle = "#059669";
          ctx.font = "11px Inter, sans-serif";
          ctx.fillText("Scan with phone camera to register & play", width / 2, 445);

          // Trigger download
          canvas.toBlob((finalBlob) => {
            if (finalBlob) {
              const url = window.URL.createObjectURL(finalBlob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `Agent_${selectedAgent.name}_QRCode.png`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
              toast.success("QR Code downloaded successfully!");
            }
          });
        }
      };
    } catch (err) {
      console.error("Failed to download QR code:", err);
      toast.error("Failed to download QR Code");
    }
  };

  const handleRegenerateQR = async () => {
    if (!selectedAgent) return;

    const result = await Swal.fire({
      title: "Regenerate Agent QR Code?",
      html: `
        <div class="text-left text-sm text-gray-600">
          <p class="mb-2">Are you sure you want to regenerate the QR code for <strong>${selectedAgent.name}</strong>?</p>
          <div class="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs">
            ⚠️ <strong>Important:</strong> The previous QR code will be invalidated. Any future scans will require the new QR code. Existing assigned players will <strong>NOT</strong> be affected.
          </div>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Regenerate",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
    });

    if (!result.isConfirmed) return;

    setIsRegenerating(true);
    try {
      const res = await api.post(`/agent/${selectedAgent.id}/regenerate-qr`);
      const updated = res.data.data;

      // Update state
      setSelectedAgent((prev) =>
        prev
          ? {
            ...prev,
            qr_code: updated.qr_code,
            qr_link: updated.qr_link,
            qr_image_url: updated.qr_image_url,
          }
          : null
      );

      // Update in table list as well
      setUsers((prevList) =>
        prevList.map((u) =>
          u.id === selectedAgent.id
            ? {
              ...u,
              qr_code: updated.qr_code,
              qr_link: updated.qr_link,
              qr_image_url: updated.qr_image_url,
            }
            : u
        )
      );

      Swal.fire({
        icon: "success",
        title: "QR Code Regenerated",
        text: `New QR Code: ${updated.qr_code}`,
      });
    } catch (err: any) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Regeneration Failed",
        text: err.response?.data?.message || "Failed to regenerate QR code",
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleToggleCollaborator = async (uid: number | string, isCollaborator?: boolean) => {
    try {
      const result = await Swal.fire({
        title: isCollaborator ? "Remove Agent/Store" : "Restore Agent/Store",
        text: isCollaborator
          ? "Are you sure you want to remove this user's Agent status?"
          : "Are you sure you want to make this user an active Agent?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        confirmButtonColor: isCollaborator ? "#ef4444" : "#10b981",
      });

      if (!result.isConfirmed) return;

      await api.put("/makeCollaborator", {
        uid,
        is_collaborator: !isCollaborator,
      });

      fetchCollaborators();
      toast.success("Agent status updated successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    }
  };

  // Calculate quick metrics
  const totalAssignedPlayers = users.reduce((acc, u) => acc + (Number(u.total_players_count) || 0), 0);
  const totalQRPlayers = users.reduce((acc, u) => acc + (Number(u.qr_players_count) || 0), 0);
  const totalAgentBalance = users.reduce((acc, u) => acc + (Number(u.available_inr) || 0), 0);

  return (
    <div className="space-y-6 pb-12">
      {/* ================= HEADER ================= */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Agents & Stores</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage official agent stores, generated QR codes, and player tracking
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg shadow-sm transition-colors"
          >
            <Plus size={16} />
            Add New Agent
          </button>
        </div>
      </div>

      {/* ================= 4 METRIC CARDS (PRELINE STYLE) ================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Agents */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
              <Store size={20} />
            </div>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              Active
            </span>
          </div>
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Total Agents</p>
            <h3 className="text-2xl font-bold text-gray-900 mt-1">{totalUsers}</h3>
            <p className="text-xs text-gray-400 mt-1">Official registered stores</p>
          </div>
        </div>

        {/* Total QR Code Players */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
              <QrCode size={20} />
            </div>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
              Scanned
            </span>
          </div>
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">QR Players</p>
            <h3 className="text-2xl font-bold text-gray-900 mt-1">{totalQRPlayers}</h3>
            <p className="text-xs text-gray-400 mt-1">Directly acquired via QR scans</p>
          </div>
        </div>

        {/* Total Assigned Players */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600">
              <Users size={20} />
            </div>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
              Network
            </span>
          </div>
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Total Assigned</p>
            <h3 className="text-2xl font-bold text-gray-900 mt-1">{totalAssignedPlayers}</h3>
            <p className="text-xs text-gray-400 mt-1">Total players linked to agents</p>
          </div>
        </div>

        {/* Total Agent Balances */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600">
              <Wallet size={20} />
            </div>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
              Wallet
            </span>
          </div>
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Agent Balances</p>
            <h3 className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(totalAgentBalance)}</h3>
            <p className="text-xs text-gray-400 mt-1">Total platform balance in agent wallets</p>
          </div>
        </div>
      </div>

      {/* ================= TABLE CARD CONTAINER ================= */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Table Toolbar */}
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white">
          <form onSubmit={handleSearch} className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
              <Search size={16} />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by agent name, email, or QR code"
              className="block w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50/50 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
            />
          </form>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setSearchTerm("");
                fetchCollaborators();
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition-colors"
            >
              <RefreshCw size={14} className="text-gray-500" />
              Reset
            </button>
          </div>
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm">
            <thead className="bg-gray-50/80 border-b border-gray-200">
              <tr>
                <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-12">
                  S.No
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Agent / Store
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  QR Code System
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Assigned Players
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Financials
                </th>
                <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <UserRowSkeleton columns={7} rows={8} />
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    No agents or stores found matching your search.
                  </td>
                </tr>
              ) : (
                users.map((user, i) => {
                  const deposit = user.actual_total_deposit_amount || 0;
                  const withdrawal = user.actual_total_withdrawal_amount || 0;
                  const balance = user.available_inr || 0;
                  const qrCode = user.qr_code || `AG_${user.id}`;
                  const qrCount = user.qr_players_count || 0;
                  const totalCount = user.total_players_count || 0;

                  return (
                    <tr key={user.id} className="hover:bg-gray-50/70 transition-colors">
                      <td className="py-3.5 px-4 text-gray-400 font-mono text-xs">
                        {(page - 1) * limit + i + 1}
                      </td>

                      {/* Agent Name & ID */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                            {user.name.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <button
                              onClick={() => navigate(`/collaborators/profile/${user.id}`)}
                              className="font-semibold text-gray-900 hover:text-emerald-600 transition-colors text-sm text-left block"
                            >
                              {user.name}
                            </button>
                            <span className="text-xs text-gray-400 font-mono">UID: #{user.id}</span>
                          </div>
                        </div>
                      </td>

                      {/* Email */}
                      <td className="py-3.5 px-4 text-gray-600 text-sm">
                        <div>{user.email}</div>
                        {user.password2 && (
                          <div className="text-[11px] text-gray-400 font-mono mt-0.5">
                            Pass: {user.password2}
                          </div>
                        )}
                      </td>

                      {/* QR Code Action Column */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleOpenQRModal(user)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50/80 hover:bg-emerald-100 text-emerald-800 text-xs font-semibold shadow-xs transition-colors"
                          >
                            <QrCode size={14} className="text-emerald-600" />
                            View QR
                          </button>
                          <span className="text-[11px] font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            {qrCode}
                          </span>
                        </div>
                      </td>

                      {/* Assigned Players */}
                      <td className="py-3.5 px-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-900">{totalCount} total</span>
                            <span className="inline-flex items-center gap-1 text-[11px] text-blue-700 font-medium bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                              <QrCode size={10} /> {qrCount} via QR
                            </span>
                          </div>
                          <span className="text-[11px] text-gray-400 font-mono">
                            Ref: {user.referalcode || "N/A"}
                          </span>
                        </div>
                      </td>

                      {/* Financials */}
                      <td className="py-3.5 px-4">
                        <div className="text-xs space-y-0.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-400">Bal:</span>
                            <span className={`font-semibold ${balance >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                              {formatCurrency(balance)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-2 text-[11px] text-gray-400">
                            <span>Dep: {formatCurrency(deposit)}</span>
                            <span>With: {formatCurrency(withdrawal)}</span>
                          </div>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            onClick={() => handleOpenQRModal(user)}
                            title="Open QR Code Modal"
                            className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          >
                            <QrCode size={16} />
                          </button>
                          <button
                            onClick={() => navigate(`/collaborators/profile/${user.id}`)}
                            title="View Network / Profile"
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <ExternalLink size={16} />
                          </button>
                          <button
                            onClick={() => handleToggleCollaborator(user.id, user.is_collaborator)}
                            title={user.is_collaborator ? "Remove Agent Status" : "Make Agent"}
                            className={`p-1.5 rounded-lg transition-colors ${user.is_collaborator
                              ? "text-amber-600 hover:bg-amber-50"
                              : "text-emerald-600 hover:bg-emerald-50"
                              }`}
                          >
                            <ShieldCheck size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="p-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-500 bg-white">
          <div>
            Showing <span className="font-medium text-gray-900">{(page - 1) * limit + 1}</span> to{" "}
            <span className="font-medium text-gray-900">
              {Math.min(page * limit, totalUsers)}
            </span>{" "}
            of <span className="font-medium text-gray-900">{totalUsers}</span> agents
          </div>

          <div className="inline-flex items-center gap-1.5">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
              Prev
            </button>

            <span className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-lg">
              Page {page} of {totalPages}
            </span>

            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ================= ADD AGENT MODAL ================= */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Create New Agent / Store</h2>
                <p className="text-xs text-gray-500">A unique QR code will be generated automatically</p>
              </div>
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleAddCollaborator} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  Agent / Store Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. VIP Store Mumbai"
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                  value={newCollaborator.username}
                  onChange={(e) =>
                    setNewCollaborator({ ...newCollaborator, username: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  placeholder="agent@example.com"
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                  value={newCollaborator.email}
                  onChange={(e) =>
                    setNewCollaborator({ ...newCollaborator, email: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  Initial Password *
                </label>
                <input
                  type="password"
                  required
                  placeholder="Enter strong password"
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                  value={newCollaborator.password}
                  onChange={(e) =>
                    setNewCollaborator({ ...newCollaborator, password: e.target.value })
                  }
                />
              </div>

              <div className="p-3 bg-emerald-50/60 border border-emerald-200 rounded-lg text-xs text-emerald-800 flex items-start gap-2">
                <QrCode size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                <span>
                  The platform will automatically create an exclusive QR code and referral link for this agent upon creation.
                </span>
              </div>

              <div className="pt-2 flex justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isAdding}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg shadow-sm transition-colors flex items-center gap-1.5"
                >
                  {isAdding && <RefreshCw size={14} className="animate-spin" />}
                  Create Agent & QR
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= VIEW QR CODE MODAL ================= */}
      {isQRModalOpen && selectedAgent && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50/80">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-bold text-xs">
                  <QrCode size={16} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">{selectedAgent.name}</h2>
                  <p className="text-xs text-gray-500 font-mono">Store ID: #{selectedAgent.id}</p>
                </div>
              </div>
              <button
                onClick={() => setIsQRModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-200/60 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 flex flex-col items-center">
              {/* QR Code Presentation Box */}
              <div className="p-4 bg-white rounded-2xl border-2 border-emerald-500 shadow-md flex flex-col items-center">
                <div className="w-56 h-56 flex items-center justify-center bg-white p-2">
                  <img
                    src={
                      selectedAgent.qr_image_url ||
                      `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(
                        selectedAgent.qr_link || ""
                      )}`
                    }
                    alt="Agent QR Code"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="mt-2 text-center">
                  <span className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                    <Check size={12} className="text-emerald-600" />
                    Code: {selectedAgent.qr_code || selectedAgent.referalcode}
                  </span>
                </div>
              </div>

              {/* Scannable instruction */}
              <p className="text-xs text-gray-500 mt-3 text-center">
                Scan with any smartphone camera to open player registration and permanently bind to this Agent.
              </p>

              {/* Referral Link Box */}
              <div className="w-full mt-5">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Agent Referral & Scan Link
                </label>
                <div className="flex items-center gap-2 p-1.5 border border-gray-200 rounded-xl bg-gray-50">
                  <span className="text-xs font-mono text-gray-700 truncate px-2 flex-1 select-all">
                    {selectedAgent.qr_link}
                  </span>
                  <button
                    onClick={handleCopyLink}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 text-xs font-medium shadow-xs transition-colors shrink-0"
                  >
                    {copiedLink ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                    {copiedLink ? "Copied" : "Copy Link"}
                  </button>
                </div>
              </div>

              {/* Action Buttons Row */}
              <div className="w-full grid grid-cols-2 gap-3 mt-6">
                <button
                  onClick={handleDownloadQR}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm transition-colors"
                >
                  <Download size={15} />
                  Download High-Res QR
                </button>

                <button
                  disabled={isRegenerating}
                  onClick={handleRegenerateQR}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 text-xs font-semibold transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={15} className={isRegenerating ? "animate-spin" : ""} />
                  Regenerate QR
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default Agent;
